import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, makeTranslator, LANG_LABELS, SUPPORTED_LANGS } from '../hooks/useLocale.jsx';
import { ReferralService } from '../services/referral-service.js';
import { useCreditConfig } from '../services/credit-config.js';
import { APP_STORE_URL, playStoreUrl, mobileOS } from '../services/app-links.js';
import { publicOrigin } from '../services/platform-service.js';
import { logEvent, analytics } from '../firebase.js';
import { HERO_IMAGE } from '../services/hero-image.js';
import { INTERIOR_STYLES, EXTERIOR_STYLES, GARDEN_STYLES } from '../shared/style-catalog.js';
import { styleThumb, styleThumbFallback } from '../utils/style-thumb.js';
import { landingPath } from '../utils/landing-paths.js';
import '../styles/landing.css';

// Marketing landing — public, full-bleed (no app header / nav). Lives at
// /landing (언어 자동) 와 /ko · /ja … (언어 고정) 에 뜬다. archelier.co 의 / 는 /landing 으로 간다.
//
// 2026-11 — 히어로 한 장뿐이던 페이지에 본문을 붙였다 (비포·애프터, 사용법, 스타일, 모드, 부분 편집,
// Gio, 가격, FAQ). 이 컴포넌트는 **정적 HTML 로도 구워진다** (scripts/prerender-landing.mjs, 언어마다
// 한 장) — 검색엔진·AI 크롤러가 받는 본문이 곧 이 화면이다. 그래서:
//   · 첫 렌더에서 window/localStorage 에 기대지 말 것 (node 에서 돈다). 효과는 useEffect 안에서.
//   · 아이콘 폰트(material-symbols)를 쓰지 말 것 — 정적 본문에 'check_circle' 같은 글자가 남는다. SVG 로.
//   · 문구는 전부 로케일 lp.* — JSON-LD FAQ 도 같은 키에서 만든다.

// 비포·애프터 — 운영자 본인 계정의 실제 결과 (public/marketing/showcase/, 원본은 Firestore designs).
const SHOWCASE = [
  { key: 'room', tab: 'tabRoom', cap: 'capRoom', w: 726, h: 1000 },
  { key: 'empty', tab: 'tabEmpty', cap: 'capEmpty', w: 1000, h: 1000 },
  { key: 'sketch', tab: 'tabSketch', cap: 'capSketch', w: 1000, h: 576 },
  { key: 'plan', tab: 'tabPlan', cap: 'capPlan', w: 1000, h: 648 },
  { key: 'exterior', tab: 'tabExterior', cap: 'capExterior', w: 781, h: 1000 },
];
// 슬라이더 최대 높이(px) — 세로 사진이 데스크톱 화면을 넘지 않게.
const BA_MAX_H = 620;

// 스타일 줄에서 빼는 키 — 시즌(기간 한정)과 특수 모드(아래 "모드" 절에서 따로 소개).
const NOT_PRESET = new Set(['custom', 'reference', 'floorplan', 'cleanup', 'layout']);
const presetKeys = (catalog) => Object.keys(catalog).filter((k) => !NOT_PRESET.has(k) && !catalog[k].season);
const STYLE_ROWS = [
  { cat: 'interior', label: 'stylesInterior', keys: presetKeys(INTERIOR_STYLES) },
  { cat: 'exterior', label: 'stylesExterior', keys: presetKeys(EXTERIOR_STYLES) },
  { cat: 'garden', label: 'stylesGarden', keys: presetKeys(GARDEN_STYLES) },
];

const MODES = ['Custom', 'Reference', 'Cleanup', 'Layout', 'Plan'];
const FAQ_COUNT = 9;

export function Landing({ lang: routeLang } = {}) {
  const ctx = useLocale();
  // /ko 처럼 주소가 언어를 정하면 그 언어로만 그린다 (저장된 앱 언어는 건드리지 않는다 —
  // "웹에서 계속하기" 나 언어 메뉴를 누를 때만 저장한다).
  const lang = routeLang || ctx.lang;
  const { t, styleName } = useMemo(
    () => (routeLang ? makeTranslator(routeLang) : { t: ctx.t, styleName: ctx.styleName }),
    [routeLang, ctx.t, ctx.styleName],
  );
  const lp = (k, p) => t(`lp.${k}`, p);

  const creditCfg = useCreditConfig();
  const [langOpen, setLangOpen] = useState(false);
  // 정적 사본이 먼저 그려졌으면 같은 히어로 사진을 이어 쓴다 — 세션마다 사진이 도는데
  // (hero-image.js) 여기서 바뀌면 JS 가 뜨는 순간 사진이 툭 바뀐다.
  const [hero] = useState(() => {
    if (typeof document === 'undefined') return HERO_IMAGE;
    return document.querySelector('[data-prerender] .lp-bg')?.getAttribute('src') || HERO_IMAGE;
  });
  // main.jsx 의 capturePendingReferral() 이 ?ref= 를 이미 localStorage 로
  // 옮기고 주소창에서 지운 뒤라, URL 이 아니라 stash 에서 읽는다.
  const [pendingRef] = useState(() => ReferralService.getPendingReferral());
  const [refCopied, setRefCopied] = useState(false);
  // 초대 카드는 기기마다 다르다 (아래 lp-invite 주석).
  const refOS = mobileOS();
  const [refQr, setRefQr] = useState(null);
  useEffect(() => {
    if (!pendingRef || refOS) return;
    let alive = true;
    // 데스크톱에서만 — QR 라이브러리를 모바일 첫 화면에 싣지 않는다.
    import('qrcode')
      .then(({ default: QR }) => QR.toDataURL(`${publicOrigin()}/?ref=${encodeURIComponent(pendingRef)}`, { margin: 1, width: 240 }))
      .then((url) => { if (alive) setRefQr(url); })
      .catch(() => { /* QR 없이도 코드·문구는 보인다 */ });
    return () => { alive = false; };
  }, [pendingRef, refOS]);
  // 데스크톱 QR — 폰 카메라로 찍으면 같은 언어의 랜딩(스토어 배지)이 열린다. 모바일 기기면 안 만든다
  // (index.html head 가 붙이는 html.lp-ua-mobile). 정적 사본엔 QR 이 없고 같은 크기의 빈 칸만 있다.
  const [appQr, setAppQr] = useState(null);
  useEffect(() => {
    if (document.documentElement.classList.contains('lp-ua-mobile')) return undefined;
    let alive = true;
    import('qrcode')
      .then(({ default: QR }) => QR.toDataURL(`https://archelier.co${landingPath(lang)}`, {
        margin: 0, width: 176, color: { dark: '#121212', light: '#FFFFFF' },
      }))
      .then((url) => { if (alive) setAppQr(url); })
      .catch(() => { /* QR 없이도 배지는 있다 */ });
    return () => { alive = false; };
  }, [lang]);

  // 랜딩 → 설치 전환을 재려고 배지 클릭을 남긴다 (2.2.1 전엔 기록이 없어
  // 앱 게이트가 설치를 늘렸는지 판단할 수 없었다). 실제 설치 여부는 스토어
  // 콘솔이 안다 — 이건 "가려고 누른 사람" 까지. surface 로 히어로/맨 아래를 가른다.
  const logStoreClick = (store, surface) => logEvent(analytics, 'store_badge_clicked', {
    store, surface, device: mobileOS() || 'desktop', lang,
  });
  const onWebStart = (surface) => {
    if (routeLang) ctx.setLang(routeLang);
    logEvent(analytics, 'landing_web_start', { device: mobileOS() || 'desktop', lang, surface });
  };

  const copyRefCode = async () => {
    try {
      await navigator.clipboard.writeText(pendingRef);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 1800);
    } catch { /* 클립보드 거부 — 코드는 화면에 보이므로 치명적이지 않다 */ }
  };

  const stores = (surface) => (
    <div className="lp-stores">
      <a className="lp-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => logStoreClick('app_store', surface)}>
        <AppleGlyph />
        <span className="lp-store-text">
          <small>{t('landingStoreOn')}</small>
          <strong>App Store</strong>
        </span>
      </a>
      <a className="lp-store" href={playStoreUrl(pendingRef)} target="_blank" rel="noopener noreferrer" onClick={() => logStoreClick('google_play', surface)}>
        <PlayGlyph />
        <span className="lp-store-text">
          <small>{t('landingStoreGet')}</small>
          <strong>Google Play</strong>
        </span>
      </a>
    </div>
  );

  // 데스크톱은 **웹이 주 행동**이다 (2026-11-01). 데스크톱 방문자는 그 자리에서 앱을 설치할 수 없다 —
  // 9/22 에 웹 링크를 뺐더니 (02e06d5) 데스크톱 웹 생성자가 2주 17명 → 3명, 설치는 그대로였다.
  // 그래서 "브라우저에서 해보기" 를 큰 버튼으로, 앱은 QR + 배지로 한 칸 아래에 둔다.
  // 모바일은 그대로 스토어만 — 웹 버튼을 두면 앱 게이트를 통째로 우회하는 정문이 된다.
  //
  // 어느 쪽을 보일지는 **CSS 가** 정한다 (.lp-desk / html.lp-ua-mobile, index.html head 스크립트).
  // JS 로 가르면 정적 사본(기기를 모른다)과 React 화면이 달라 JS 가 뜨는 순간 버튼이 바뀐다.
  // 설치한 PWA 는 데스크톱 쪽으로 친다 (이미 설치한 사람이다).
  const getApp = (surface) => (
    <div className="lp-get">
      <div className="lp-qr lp-desk" aria-hidden="true">
        {appQr && <img src={appQr} alt="" width="88" height="88" />}
      </div>
      <div className="lp-get-body">
        <p className="lp-get-label lp-desk">{lp('orApp')}</p>
        {stores(surface)}
        <p className="lp-qr-hint lp-desk">{lp('qrHint')}</p>
      </div>
    </div>
  );
  const webButton = (surface) => (
    <Link className="lp-web-btn lp-desk" to="/create" onClick={() => onWebStart(surface)}>
      {lp('webCta')}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>
    </Link>
  );

  return (
    <div className="landing" lang={lang}>
      <header className="lp-nav">
        <span className="lp-brand">archelier<span className="lp-brand-dot">.</span></span>
        <div className="lp-lang">
          <button
            type="button"
            className="lp-lang-btn"
            onClick={() => setLangOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={langOpen}
            aria-label={lp('langLabel')}
          >
            <GlobeGlyph />
            <span>{LANG_LABELS[lang]}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {langOpen && (
            <ul className="lp-lang-menu" role="listbox">
              {SUPPORTED_LANGS.map(code => (
                <li key={code}>
                  {/* 언어마다 주소가 있다 (/ko, /ja …) — 링크로 두어야 크롤러도 따라간다. */}
                  <Link
                    to={landingPath(code)}
                    role="option"
                    aria-selected={lang === code}
                    hrefLang={code}
                    onClick={() => { ctx.setLang(code); setLangOpen(false); }}
                  >
                    {LANG_LABELS[code]}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </header>

      <main>
        <section className="lp-hero">
          {/* 폰 목업 3장 → 로그인 화면과 같은 인테리어 사진 한 장. 앱을 켜면
             나오는 첫 화면과 광고 페이지가 같은 장면이라 인상이 이어진다. */}
          <img className="lp-bg" src={hero} alt="" aria-hidden="true" fetchpriority="high" />
          <div className="lp-scrim" aria-hidden="true" />

          <div className="lp-copy">
            <p className="lp-eyebrow">{lp('heroEyebrow')}</p>
            <h1 className="lp-headline">{t('landingHeadline')}</h1>
            <p className="lp-tagline">{t('landingTagline')}</p>

            {/* 초대 링크로 온 방문자. 웹에서 바로 가입하면 stash 된 코드가 자동으로 적용되지만,
               여기서 앱을 설치하면 브라우저 localStorage 가 앱으로 안 넘어간다. 그래서 기기마다:
               - Android: Play 버튼이 코드를 리퍼러로 달고 간다 (playStoreUrl) — 설치한 앱이 받는다.
                 Play 를 거치지 않고 깔았을 때를 위해 코드와 "설정 → 친구 초대" 안내도 남긴다.
               - iOS: 넘길 통로가 없다. 설치한 뒤 같은 링크를 다시 누르면 유니버설 링크로 앱이 열리며
                 코드가 들어간다 (AASA 의 /?ref=). 코드는 안 보여준다 — 앱에 넣을 칸이 없다 (2.4.1, 3.1.1).
               - 데스크톱: 여기서 가입하면 끝. 폰 앱으로 받으려면 같은 링크를 QR 로 연다. */}
            {pendingRef && (
              <div className={`lp-invite${refQr ? ' has-qr' : ''}`}>
                <div className="lp-invite-body">
                  <p className="lp-invite-title">{t('landingInviteTitle', { n: creditCfg.inviteReward })}</p>
                  {refOS !== 'ios' && (
                    <button
                      type="button"
                      className="lp-invite-code"
                      onClick={copyRefCode}
                      title={t('copyCode')}
                    >
                      <span>{pendingRef}</span>
                      <i className="material-symbols-rounded">{refCopied ? 'check' : 'content_copy'}</i>
                    </button>
                  )}
                  <p className="lp-invite-hint">
                    {t(refOS === 'ios' ? 'landingInviteAutoIos' : refOS === 'android' ? 'landingInviteAutoAndroid' : 'landingInviteDesktop')}
                  </p>
                </div>
                {refQr && <img className="lp-invite-qr" src={refQr} alt="" width="120" height="120" />}
              </div>
            )}

            {webButton('landing')}
            {getApp('landing')}
          </div>

          <a className="lp-scroll-cue" href="#showcase">
            <span>{lp('scrollCue')}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
          </a>
        </section>

        <Showcase lp={lp} />

        <section className="lp-section" id="how">
          <div className="lp-wrap">
            <SectionHead eyebrow={lp('howEyebrow')} title={lp('howTitle')} />
            <ol className="lp-steps">
              {[1, 2, 3].map((n) => (
                <li key={n} className="lp-step">
                  <span className="lp-step-n" aria-hidden="true">{n}</span>
                  <h3>{lp(`how${n}Title`)}</h3>
                  <p>{lp(`how${n}Body`)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="lp-section lp-section-tint" id="styles">
          <div className="lp-wrap">
            <SectionHead eyebrow={lp('stylesEyebrow')} title={lp('stylesTitle')} body={lp('stylesBody')} />
          </div>
          {STYLE_ROWS.map((row) => (
            <div key={row.cat} className="lp-style-group">
              <h3 className="lp-wrap lp-style-label">{lp(row.label)} <span>{row.keys.length}</span></h3>
              <ul className="lp-style-row">
                {row.keys.map((key) => (
                  <li key={key} className="lp-style">
                    <img
                      src={styleThumb(row.cat, key)}
                      onError={styleThumbFallback(row.cat, key)}
                      alt=""
                      width="200"
                      height="200"
                      loading="lazy"
                      decoding="async"
                    />
                    <span>{styleName(key, row.cat)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lp-wrap">
            <Link className="lp-text-link" to="/gallery">{lp('stylesCta')} →</Link>
          </div>
        </section>

        <section className="lp-section" id="modes">
          <div className="lp-wrap">
            <SectionHead eyebrow={lp('modesEyebrow')} title={lp('modesTitle')} />
            <ul className="lp-modes">
              {MODES.map((m, i) => (
                <li key={m} className="lp-mode">
                  <span className="lp-mode-n" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{lp(`mode${m}Title`)}</h3>
                  <p>{lp(`mode${m}Body`)}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lp-section lp-section-tint" id="edit">
          <div className="lp-wrap lp-split">
            <figure className="lp-edit-figure">
              <img src="/marketing/showcase/room-after.webp" alt="" width="726" height="1000" loading="lazy" decoding="async" />
              {/* 앱의 "직접 칠하기" 처럼 소파 자리에 칠한 자국을 얹는다 (사진 726×1000 기준 좌표). */}
              <svg className="lp-edit-mark" viewBox="0 0 726 1000" preserveAspectRatio="none" aria-hidden="true">
                <path d="M30 640 C 70 575, 250 560, 318 610 C 365 650, 352 860, 300 905 C 230 960, 60 955, 18 900 C -10 850, -5 690, 30 640 Z" />
              </svg>
              <figcaption className="lp-edit-prompt">
                <span className="lp-edit-dot" aria-hidden="true" />
                {lp('editPrompt')}
              </figcaption>
            </figure>
            <div className="lp-split-copy">
              <SectionHead eyebrow={lp('editEyebrow')} title={lp('editTitle')} body={lp('editBody')} />
              <div className="lp-note">
                <h3>{lp('shopTitle')}</h3>
                <p>{lp('shopBody')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section" id="gio">
          <div className="lp-wrap lp-split lp-split-rev">
            <div className="lp-split-copy">
              <SectionHead eyebrow={lp('gioEyebrow')} title={lp('gioTitle')} body={lp('gioBody')} />
            </div>
            <div className="lp-chat" aria-hidden="true">
              <div className="lp-chat-head">
                <span className="lp-avatar">G</span>
                <span><strong>Gio</strong><small>{lp('gioRole')}</small></span>
              </div>
              <p className="lp-bubble lp-bubble-me">{lp('gioUser')}</p>
              <p className="lp-bubble">{lp('gioReply')}</p>
              <div className="lp-chat-card">
                <img src={styleThumb('interior', 'japandi')} alt="" width="72" height="72" loading="lazy" />
                <div>
                  <small>{styleName('japandi', 'interior')}</small>
                  <strong>{lp('gioCardTitle')}</strong>
                  <p>{lp('gioCardBody')}</p>
                  <span className="lp-chat-btn">{lp('gioCardAction')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section lp-section-tint" id="pricing">
          <div className="lp-wrap lp-split">
            <div className="lp-split-copy">
              <SectionHead eyebrow={lp('priceEyebrow')} title={lp('priceTitle')} />
              <ul className="lp-checks">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n}><CheckGlyph />{lp(`price${n}`)}</li>
                ))}
              </ul>
            </div>
            <div className="lp-note lp-note-card">
              <LockGlyph />
              <h3>{lp('privacyTitle')}</h3>
              <p>{lp('privacyBody')}</p>
            </div>
          </div>
        </section>

        <section className="lp-section" id="faq">
          <div className="lp-wrap lp-faq-wrap">
            <SectionHead title={lp('faqTitle')} />
            <div className="lp-faq">
              {Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map((n) => (
                <details key={n} className="lp-faq-item">
                  <summary>{lp(`faq${n}Q`)}</summary>
                  <p>{lp(`faq${n}A`)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-cta">
          <div className="lp-wrap lp-cta-inner">
            <h2>{lp('ctaTitle')}</h2>
            <p>{lp('ctaBody')}</p>
            {webButton('landing_bottom')}
            {getApp('landing_bottom')}
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-inner">
          <span className="lp-foot-brand">archelier</span>
          <nav aria-label={lp('langLabel')} className="lp-foot-langs">
            {SUPPORTED_LANGS.map((code) => (
              <Link
                key={code}
                to={landingPath(code)}
                hrefLang={code}
                aria-current={code === lang ? 'page' : undefined}
                onClick={() => ctx.setLang(code)}
              >
                {LANG_LABELS[code]}
              </Link>
            ))}
          </nav>
          <nav className="lp-foot-legal">
            <Link to="/support">{t('support')}</Link>
            <Link to="/privacy">{t('privacy')}</Link>
            <Link to="/terms">{t('terms')}</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function SectionHead({ eyebrow, title, body }) {
  return (
    <div className="lp-head">
      {eyebrow && <p className="lp-kicker">{eyebrow}</p>}
      <h2>{title}</h2>
      {body && <p className="lp-lede">{body}</p>}
    </div>
  );
}

function Showcase({ lp }) {
  const [active, setActive] = useState(0);
  const item = SHOWCASE[active];
  return (
    <section className="lp-section lp-showcase" id="showcase">
      <div className="lp-wrap lp-split">
        <div className="lp-split-copy">
          <SectionHead eyebrow={lp('showEyebrow')} title={lp('showTitle')} body={lp('showBody')} />
          <div className="lp-tabs" role="tablist">
            {SHOWCASE.map((s, i) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={i === active}
                className={`lp-tab${i === active ? ' is-active' : ''}`}
                onClick={() => setActive(i)}
              >
                {lp(s.tab)}
              </button>
            ))}
          </div>
          <p className="lp-caption">{lp(item.cap)}</p>
        </div>
        <BeforeAfter key={item.key} item={item} lp={lp} />
      </div>
    </section>
  );
}

// 앱 상세 화면의 비교 슬라이더와 같은 동작. 가로로 끌면 비교, 세로로 끌면 페이지가
// 스크롤된다 (touch-action: pan-y). 키보드는 손잡이에서 ← →.
function BeforeAfter({ item, lp }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const moveTo = (clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  };
  const src = (side) => `/marketing/showcase/${item.key}-${side}.webp`;
  const caption = lp(item.cap);
  return (
    <div
      ref={ref}
      className="lp-ba"
      style={{ width: `min(100%, ${Math.round((BA_MAX_H * item.w) / item.h)}px)`, aspectRatio: `${item.w} / ${item.h}` }}
      onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); moveTo(e.clientX); }}
      onPointerMove={(e) => { if (dragging.current) moveTo(e.clientX); }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
    >
      <img className="lp-ba-img" src={src('after')} alt={`${caption} (${lp('after')})`} width={item.w} height={item.h} draggable="false" decoding="async" />
      <img
        className="lp-ba-img"
        src={src('before')}
        alt={`${caption} (${lp('before')})`}
        width={item.w}
        height={item.h}
        draggable="false"
        decoding="async"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <span className="lp-ba-tag lp-ba-tag-l">{lp('before')}</span>
      <span className="lp-ba-tag lp-ba-tag-r">{lp('after')}</span>
      <div className="lp-ba-line" style={{ left: `${pos}%` }}>
        <span
          className="lp-ba-handle"
          role="slider"
          tabIndex={0}
          aria-label={`${lp('before')} / ${lp('after')}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); setPos((p) => Math.max(0, p - 5)); }
            if (e.key === 'ArrowRight') { e.preventDefault(); setPos((p) => Math.min(100, p + 5)); }
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 6 3 12 9 18" /><polyline points="15 6 21 12 15 18" />
          </svg>
        </span>
      </div>
    </div>
  );
}

function GlobeGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.6 2.8 2.6 15.2 0 18M12 3c-2.6 2.8-2.6 15.2 0 18" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg className="lp-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="5 12.5 10 17 19 7" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg className="lp-lock" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg width="20" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.65-2.323-7.34 0-4.31 2.797-6.6 5.552-6.6 1.46 0 2.68.96 3.6.96.87 0 2.23-1.02 3.96-1.02.66 0 3.012.06 4.55 2.29-.12.07-2.69 1.57-2.69 4.76 0 3.74 3.25 5.06 3.34 5.09z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width="20" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#00D3FF" d="M3.6 2.3c-.4.2-.6.6-.6 1.1v17.2c0 .5.2.9.6 1.1l9.4-9.7L3.6 2.3z" />
      <path fill="#FFCE00" d="M17.3 9.1l-3.5-2-3.4 3.5 3.4 3.5 3.5-2c1.1-.6 1.1-2.4 0-3z" />
      <path fill="#FF3D44" d="M3.6 2.3l9.4 9.7 3.4-3.5L5.6 2.1c-.7-.4-1.5-.3-2 .2z" />
      <path fill="#00E676" d="M3.6 21.7c.5.5 1.3.6 2 .2l10.8-6.4-3.4-3.5-9.4 9.7z" />
    </svg>
  );
}

export default Landing;
